<template>
  <default>
    <div class="tw-w-full">
      <bread-crumb :breadcrumbs="breadcrumbs" />

      <div>
          <div class="policy-line-tabs" role="tablist" aria-label="Policy type">
            <button
              v-for="line in lineTabs"
              :key="line.key"
              type="button"
              role="tab"
              class="policy-line-tab"
              :class="{ 'policy-line-tab--active': selectedLine === line.key }"
              :aria-selected="selectedLine === line.key"
              @click="selectedLine = line.key"
            >
              {{ line.label }}
            </button>
          </div>

          <health-policy-section
            v-if="selectedLine === 'medical'"
            :policies="healthPolicies"
            :loading="healthLoading"
            :error="healthError"
            :missing-id="!nationalId"
            @retry="loadHealthPolicies"
          />

          <div v-if="selectedLine === 'general' && activeProductPending" class="policy-shimmer" aria-hidden="true">
            <div v-for="n in 3" :key="`policy-shimmer-${n}`" class="policy-shimmer-card">
              <div class="shimmer policy-shimmer-icon"></div>
              <div class="policy-shimmer-copy">
                <div class="shimmer policy-shimmer-line policy-shimmer-line--title"></div>
                <div class="shimmer policy-shimmer-line"></div>
              </div>
              <div class="shimmer policy-shimmer-block"></div>
            </div>
          </div>

          <div v-if="selectedLine === 'general' && showProductTabs" class="policy-product-toggles" role="tablist" aria-label="Filter by product">
            <button
              v-for="tab in productTabs"
              :key="tab.key"
              type="button"
              role="tab"
              class="policy-product-toggle"
              :class="{ 'policy-product-toggle--active': selectedProduct === tab.key }"
              :aria-selected="selectedProduct === tab.key"
              @click="selectedProduct = tab.key; productTouched = true"
            >
              <v-icon size="18" class="tw-mr-1">{{ tab.icon }}</v-icon>
              {{ tab.label }}
              <span v-if="!tab.loading" class="policy-product-toggle__count">{{ tab.count }}</span>
            </button>
          </div>

          <div v-if="selectedLine === 'general' && selectedProduct === 'motor' && !motorPending" class="pa-policies-section">
            <header class="pa-section-header">
              <div>
                <h2 class="pa-section-title">Motor Insurance</h2>
                <p class="pa-section-subtitle">
                  {{ sortedMotorPolicies.length }} active {{ sortedMotorPolicies.length === 1 ? 'policy' : 'policies' }}
                </p>
                <p class="pa-section-doc-hint">Documents are emailed when ready. Use “Check for document” on any policy.</p>
              </div>
            </header>

            <v-card
              v-for="(policy, i) in sortedMotorPolicies"
              :id="policyCardId('motor', policy)"
              :key="`motor-${policy.policyNo || i}`"
              :class="['pa-policy-card', 'pa-policy-card--motor', { 'policy-card--focused': isPolicyFocused('motor', policy) }]"
              flat
            >
              <div class="pa-policy-card__body">
                <div class="pa-policy-card__hero">
                  <div class="pa-policy-card__icon pa-policy-card__icon--motor">
                    <v-icon size="36" color="#184679">mdi-car-outline</v-icon>
                  </div>
                  <div class="pa-policy-card__intro">
                    <div class="pa-policy-card__title-row">
                      <h3 class="pa-policy-card__title">{{ motorCoverTitle(policy) }}</h3>
                      <span class="pa-policy-card__status">Active</span>
                    </div>
                    <p class="pa-policy-card__option">{{ motorVehicleSummary(policy) }}</p>
                  </div>
                </div>

                <div class="pa-policy-card__details">
                  <div class="pa-detail-block">
                    <span class="pa-detail-label">Policy number</span>
                    <span class="pa-detail-value pa-detail-value--mono">{{ formatPolicyNumber(policy.policyNo) }}</span>
                  </div>
                  <div class="pa-detail-block">
                    <span class="pa-detail-label">Cover period</span>
                    <span class="pa-detail-value">
                      {{ formatPaDate(policy.wef) }} – {{ formatPaDate(policy.wet) }}
                    </span>
                  </div>
                  <div class="pa-detail-block pa-detail-block--premium">
                    <span class="pa-detail-label">Annual premium</span>
                    <span class="pa-detail-value pa-detail-value--premium">
                      KES {{ Number(policy.totalPremium || policy.premium || 0).toLocaleString('en-KE') }}
                    </span>
                  </div>
                </div>

                <div class="pa-policy-card__actions">
                  <policy-documents-panel
                    v-if="hasPolicyDocLookup('motor', policy) || !!motorDocLoading[policy.policyNo]"
                    :policy-number="policyNoForDocs(policy.policyNo)"
                    :policy-document="motorDocByPolicy[policy.policyNo]"
                    :loading="!!motorDocLoading[policy.policyNo]"
                    :allow-retry="true"
                    list-mode
                    embedded
                    :show-when-pending="hasPolicyDocLookup('motor', policy)"
                    @retry="loadMotorPolicyDocument(policy.policyNo)"
                  />
                  <button
                    v-else-if="policy.policyNo"
                    type="button"
                    class="pa-doc-fetch-btn"
                    @click="loadMotorPolicyDocument(policy.policyNo)"
                  >
                    Check for policy document
                  </button>

                  <router-link
                    :id="`view-policy-${i}`"
                    :to="{ name: 'SinglePolicy', params: { policyNo: policy.policyNo } }"
                    class="motor-policy-view-link"
                  >
                    View policy details
                  </router-link>
                  <router-link
                    v-if="isRenewable(policy)"
                    :to="renewLocation(policy)"
                    class="motor-renew-link"
                  >
                    <v-icon x-small left color="#C2410C">mdi-autorenew</v-icon>
                    Renew policy
                  </router-link>
                </div>
              </div>
            </v-card>

            <div v-if="sortedMotorPolicies.length === 0" class="policy-product-empty">
              <v-icon size="40" color="#94a3b8">mdi-car-outline</v-icon>
              <p>No motor policies yet.</p>
            </div>
          </div>

          <div v-if="selectedLine === 'general' && selectedProduct === 'travel' && !travelPending" class="pa-policies-section">
            <header class="pa-section-header">
              <div>
                <h2 class="pa-section-title">Travel Insurance</h2>
                <p class="pa-section-subtitle">
                  {{ sortedTravelPolicies.length }} active {{ sortedTravelPolicies.length === 1 ? 'policy' : 'policies' }}
                </p>
                <p class="pa-section-doc-hint">Documents are emailed when ready. Use “Check for document” on any policy.</p>
              </div>
            </header>

            <v-card
              v-for="(policy, i) in sortedTravelPolicies"
              :id="policyCardId('travel', policy)"
              :key="`travel-${policy.quoteId || i}`"
              :class="['pa-policy-card', { 'policy-card--focused': isPolicyFocused('travel', policy) }]"
              flat
            >
              <div class="pa-policy-card__body">
                <div class="pa-policy-card__hero">
                  <div class="pa-policy-card__icon pa-policy-card__icon--travel">
                    <v-icon size="36" color="#184679">mdi-airplane</v-icon>
                  </div>
                  <div class="pa-policy-card__intro">
                    <div class="pa-policy-card__title-row">
                      <h3 class="pa-policy-card__title">Travel Insurance</h3>
                      <span class="pa-policy-card__status">Active</span>
                    </div>
                    <p class="pa-policy-card__option">{{ travelDestinationLabel(policy) }}</p>
                    <p v-if="policy.proposerName" class="pa-policy-card__proposer">{{ policy.proposerName }}</p>
                  </div>
                </div>

                <div class="pa-policy-card__details pa-policy-card__details--travel">
                  <div class="travel-detail-summary">
                    <div class="pa-detail-block">
                      <span class="pa-detail-label">Cover period</span>
                      <span class="pa-detail-value">
                        {{ formatPaDate(policy.travelStartDate) }} – {{ formatPaDate(policy.travelEndDate) }}
                      </span>
                    </div>
                    <div class="pa-detail-block pa-detail-block--premium">
                      <span class="pa-detail-label">Premium paid</span>
                      <span class="pa-detail-value pa-detail-value--premium">
                        KES {{ Number(policy.premiumKsh || policy.premium || 0).toLocaleString('en-KE') }}
                      </span>
                    </div>
                  </div>
                  <div class="travel-detail-refs">
                    <div v-if="policy.tqPolicyNumber" class="pa-detail-block">
                      <span class="pa-detail-label">Pacis policy</span>
                      <span class="pa-detail-value pa-detail-value--mono">{{ formatPolicyNumber(policy.tqPolicyNumber) }}</span>
                    </div>
                    <div v-if="policy.mapfrePolicyNumber" class="pa-detail-block">
                      <span class="pa-detail-label">Mapfre reference</span>
                      <span class="pa-detail-value pa-detail-value--mono">{{ policy.mapfrePolicyNumber }}</span>
                    </div>
                  </div>
                </div>

                <policy-documents-panel
                  v-if="hasPolicyDocLookup('travel', policy) || !!travelDocLoading[policy.tqPolicyNumber]"
                  v-show="policy.tqPolicyNumber"
                  :policy-number="policy.tqPolicyNumber"
                  :policy-document="travelDocByPolicy[policy.tqPolicyNumber]"
                  :loading="!!travelDocLoading[policy.tqPolicyNumber]"
                  :allow-retry="true"
                  list-mode
                  compact
                  embedded
                  :show-title="false"
                  :show-when-pending="hasPolicyDocLookup('travel', policy)"
                  @retry="loadTravelPolicyDocument(policy.tqPolicyNumber)"
                />
                <button
                  v-else-if="policy.tqPolicyNumber"
                  type="button"
                  class="pa-doc-fetch-btn"
                  @click="loadTravelPolicyDocument(policy.tqPolicyNumber)"
                >
                  Check for policy document
                </button>
              </div>
            </v-card>

            <div v-if="sortedTravelPolicies.length === 0" class="policy-product-empty">
              <v-icon size="40" color="#94a3b8">mdi-airplane</v-icon>
              <p>No travel policies yet.</p>
            </div>
          </div>

          <div v-if="selectedLine === 'general' && selectedProduct === 'pa' && !paPending" class="pa-policies-section">
            <header class="pa-section-header">
              <div>
                <h2 class="pa-section-title">Students Personal Accident</h2>
                <p class="pa-section-subtitle">
                  {{ sortedPaPolicies.length }} active {{ sortedPaPolicies.length === 1 ? 'policy' : 'policies' }}
                </p>
                <p class="pa-section-doc-hint">Documents are emailed when ready. Use “Check for document” on any policy.</p>
              </div>
            </header>

            <v-card
              v-for="(policy, i) in sortedPaPolicies"
              :id="policyCardId('pa', policy)"
              :key="`pa-${policy.quoteId || i}`"
              :class="['pa-policy-card', { 'policy-card--focused': isPolicyFocused('pa', policy) }]"
              flat
            >
              <div class="pa-policy-card__body">
                <div class="pa-policy-card__hero">
                  <div class="pa-policy-card__icon">
                    <v-icon size="36" color="#00AEEF">mdi-shield-account</v-icon>
                  </div>
                  <div class="pa-policy-card__intro">
                    <div class="pa-policy-card__title-row">
                      <h3 class="pa-policy-card__title">Students PA</h3>
                      <span class="pa-policy-card__status">Active</span>
                    </div>
                    <p class="pa-policy-card__option">{{ policy.optionName || 'Personal Accident' }}</p>
                    <p v-if="policy.proposerName" class="pa-policy-card__proposer">{{ policy.proposerName }}</p>
                  </div>
                </div>

                <div class="pa-policy-card__details">
                  <div class="pa-detail-block">
                    <span class="pa-detail-label">Policy number</span>
                    <span class="pa-detail-value pa-detail-value--mono">{{ formatPolicyNumber(policy.policyNumber) }}</span>
                  </div>
                  <div class="pa-detail-block">
                    <span class="pa-detail-label">Cover period</span>
                    <span class="pa-detail-value">
                      {{ formatPaDate(policy.coverFrom) }} – {{ formatPaDate(policy.coverTo) }}
                    </span>
                  </div>
                  <div class="pa-detail-block pa-detail-block--premium">
                    <span class="pa-detail-label">Premium paid</span>
                    <span class="pa-detail-value pa-detail-value--premium">
                      KES {{ Number(policy.premium || 0).toLocaleString('en-KE') }}
                    </span>
                  </div>
                </div>

                <policy-documents-panel
                  v-if="hasPolicyDocLookup('pa', policy) || !!paDocLoading[policy.policyNumber]"
                  :policy-number="policy.policyNumber"
                  :policy-document="paDocByPolicy[policy.policyNumber]"
                  :loading="!!paDocLoading[policy.policyNumber]"
                  :allow-retry="true"
                  list-mode
                  embedded
                  :show-when-pending="hasPolicyDocLookup('pa', policy)"
                  @retry="loadPaPolicyDocument(policy.policyNumber)"
                />
                <button
                  v-else-if="policy.policyNumber"
                  type="button"
                  class="pa-doc-fetch-btn"
                  @click="loadPaPolicyDocument(policy.policyNumber)"
                >
                  Check for policy document
                </button>
              </div>
            </v-card>

            <div v-if="sortedPaPolicies.length === 0" class="policy-product-empty">
              <v-icon size="40" color="#94a3b8">mdi-shield-account-outline</v-icon>
              <p>No personal accident policies yet.</p>
            </div>
          </div>

          <div v-if="selectedLine === 'general' && selectedProduct === 'documents' && !activeProductPending" class="pa-policies-section">
            <header class="pa-section-header">
              <div>
                <h2 class="pa-section-title">Policy Documents</h2>
                <p class="pa-section-subtitle">
                  {{ documentCatalogRows.length }}
                  {{ documentCatalogRows.length === 1 ? 'policy' : 'policies' }}
                </p>
                <p class="pa-section-doc-hint">
                  Expand a policy to browse its documents — Debit Note, Schedule, Policy Document, and more.
                </p>
              </div>
              <v-btn
                text
                color="primary"
                :loading="docsCatalogLoading"
                :disabled="!documentCatalogRows.length"
                @click="refreshDocumentsCatalog"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </header>

            <div v-if="documentCatalogRows.length" class="docs-catalog-list">
              <v-card
                v-for="row in documentCatalogRows"
                :key="row.id"
                flat
                class="docs-catalog-card"
                :class="{ 'docs-catalog-card--open': isCatalogExpanded(row.id) }"
              >
                <button
                  type="button"
                  class="docs-catalog-row__header"
                  :aria-expanded="isCatalogExpanded(row.id) ? 'true' : 'false'"
                  @click="toggleCatalogRow(row)"
                >
                  <div class="docs-catalog-row__chevron" aria-hidden="true">
                    <v-icon size="22" color="#64748B">
                      {{ isCatalogExpanded(row.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </div>
                  <div class="docs-catalog-row__main">
                    <span class="docs-catalog-row__product">{{ row.productLabel }}</span>
                    <h3 class="docs-catalog-row__title">{{ row.title }}</h3>
                    <p class="docs-catalog-row__meta">{{ row.meta }}</p>
                    <p class="docs-catalog-row__policy">
                      Policy No. {{ formatPolicyNumber(row.policyNumber) }}
                    </p>
                  </div>
                  <div class="docs-catalog-row__header-actions" @click.stop>
                    <v-chip
                      v-if="catalogEntry(row.id)"
                      x-small
                      :color="catalogDocCount(row.id) > 0 ? '#EFF6FF' : '#FEF3C7'"
                      :text-color="catalogDocCount(row.id) > 0 ? '#1E40AF' : '#92400E'"
                      label
                    >
                      {{ catalogDocCount(row.id) > 0
                        ? `${catalogDocCount(row.id)} ${catalogDocCount(row.id) === 1 ? 'document' : 'documents'}`
                        : 'None yet' }}
                    </v-chip>
                    <v-chip
                      v-else-if="!isCatalogExpanded(row.id)"
                      x-small
                      color="#F1F5F9"
                      text-color="#64748B"
                      label
                    >
                      Tap to open
                    </v-chip>
                    <v-btn
                      v-if="isCatalogExpanded(row.id)"
                      icon
                      small
                      color="primary"
                      :loading="isCatalogLoading(row.id)"
                      title="Refresh documents"
                      @click="loadCatalogDocument(row)"
                    >
                      <v-icon small>mdi-refresh</v-icon>
                    </v-btn>
                  </div>
                </button>

                <div v-show="isCatalogExpanded(row.id)" class="docs-catalog-panel">
                  <div v-if="isCatalogLoading(row.id) && !catalogDocuments(row.id).length" class="docs-catalog-loading">
                    <v-progress-circular indeterminate size="22" width="2" color="primary" />
                    <span>Loading documents…</span>
                  </div>

                  <div v-else-if="catalogDocuments(row.id).length" class="docs-catalog-files">
                    <div
                      v-for="doc in catalogDocuments(row.id)"
                      :key="doc.id"
                      class="docs-catalog-file"
                    >
                      <div class="docs-catalog-file__icon">
                        <v-icon size="22" color="#E53E3E">mdi-file-pdf-box</v-icon>
                      </div>
                      <div class="docs-catalog-file__info">
                        <p class="docs-catalog-file__name">{{ formatDocumentTypeLabel(doc) }}</p>
                        <p class="docs-catalog-file__meta">
                          <span v-if="doc.actualName">{{ doc.actualName }}</span>
                          <span v-if="doc.dateCreated"> · {{ doc.dateCreated }}</span>
                        </p>
                      </div>
                      <div class="docs-catalog-file__actions">
                        <v-btn
                          small
                          text
                          color="primary"
                          :loading="isDocActionLoading(doc.id, 'view')"
                          :disabled="isDocActionBusy(doc.id)"
                          @click="openCatalogFile(doc)"
                        >
                          <v-icon left x-small>mdi-eye-outline</v-icon>
                          View
                        </v-btn>
                        <v-btn
                          small
                          outlined
                          color="primary"
                          :loading="isDocActionLoading(doc.id, 'download')"
                          :disabled="isDocActionBusy(doc.id)"
                          @click="downloadCatalogFile(doc, row.policyNumber)"
                        >
                          <v-icon left x-small>mdi-download</v-icon>
                          Download
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="!isCatalogLoading(row.id)" class="docs-catalog-empty">
                    <v-chip x-small color="#FEF3C7" text-color="#92400E" class="tw-mr-2">
                      <v-icon x-small left>mdi-clock-outline</v-icon>
                      Not ready
                    </v-chip>
                    <v-btn text small color="primary" @click="loadCatalogDocument(row)">
                      Check again
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </div>

            <div v-else class="policy-product-empty">
              <v-icon size="40" color="#94a3b8">mdi-file-document-outline</v-icon>
              <p>No policies with documents yet.</p>
            </div>
          </div>

          <div class="tw-w-full tw-mt-5 md:tw-mt-10" v-if="selectedLine === 'general' && !sourcesLoading && !hasAnyPolicies">
          <v-card class="tw-w-full tw-flex tw-flex-wrap tw-text-center tw-justify-center tw-py-24">
          <div class="tw-w-full tw-flex tw-justify-center">
            <v-icon size="100">mdi-file</v-icon>
          </div>
          <p class="tw-text-2xl tw-mt-10">No Policies yet !</p>
          </v-card>
          </div>
      </div>
    </div>
  </default>
</template>
<script>
import BreadCrumb from '@/components/dashboard/BreadCrumb';
import Default from '@/components/layout/Default';
import PolicyDocumentsPanel from '@/components/pa/PolicyDocumentsPanel';
import HealthPolicySection from '@/components/policies/HealthPolicySection';
import fetchHealthPolicies from '@/services/healthPolicyApi';
import { listPaClientPolicies } from '@/services/agentPaApi';
import { listTravelClientPolicies } from '@/services/agentTravelApi';
import {
  downloadPolicyDocument,
  fetchPolicyDocuments,
  openPolicyDocument,
  resolvePaPolicyDocument,
} from '@/utils/policyDocumentsApi';
import moment from 'moment';
import {
  maxRecency,
  motorRecencyKey,
  paRecencyKey,
  policyFocusId,
  sortByRecency,
  travelRecencyKey,
} from '@/utils/policyRecency';
import {
  fetchMotorPolicyRecords,
  motorCoverLabel,
  motorVehicleLine,
} from '@/utils/motorPolicyList';
import { customerPolicyRenewalLocation } from '@/utils/agentRenewal';
import { mapGetters } from 'vuex';

const DOC_PREFETCH_LIMIT = 3;

export default {
  name: 'MyPolicies',
  components: {
    BreadCrumb, Default, PolicyDocumentsPanel, HealthPolicySection,
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'My Policies',
          disabled: true,
          to: { name: 'MyPolicies' },
        },
      ],
      policies: [],
      paPolicies: [],
      travelPolicies: [],
      paDocByPolicy: {},
      paDocLoading: {},
      travelDocByPolicy: {},
      travelDocLoading: {},
      motorDocByPolicy: {},
      motorDocLoading: {},
      docsCatalogLoading: false,
      docsCatalogById: {},
      docsCatalogLoadingById: {},
      docsCatalogExpandedById: {},
      docsFileActionById: {},
      selectedProduct: 'motor',
      productTouched: false,
      selectedLine: 'general',
      healthPolicies: [],
      healthLoading: false,
      healthError: '',
      motorLoading: false,
      paLoading: false,
      travelLoading: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('portalCache', ['customerClient']),
    client() {
      return this.customerClient || this.$store.getters.clientData;
    },
    hasAnyPolicies() {
      return this.policies.length > 0
        || this.paPolicies.length > 0
        || this.travelPolicies.length > 0;
    },
    motorPending() {
      return this.motorLoading && this.policies.length === 0;
    },
    travelPending() {
      return this.travelLoading && this.travelPolicies.length === 0;
    },
    paPending() {
      return this.paLoading && this.paPolicies.length === 0;
    },
    sourcesLoading() {
      return this.motorLoading || this.paLoading || this.travelLoading;
    },
    showProductTabs() {
      return this.hasAnyPolicies || !this.sourcesLoading;
    },
    activeProductPending() {
      if (this.selectedProduct === 'travel') return this.travelPending;
      if (this.selectedProduct === 'pa') return this.paPending;
      if (this.selectedProduct === 'documents') {
        return this.sourcesLoading && this.documentCatalogRows.length === 0;
      }
      return this.motorPending;
    },
    sortedMotorPolicies() {
      return sortByRecency(this.policies, motorRecencyKey);
    },
    sortedPaPolicies() {
      return sortByRecency(this.paPolicies, paRecencyKey);
    },
    sortedTravelPolicies() {
      return sortByRecency(this.travelPolicies, travelRecencyKey);
    },
    nationalId() {
      return this.authenticatedUser?.['custom:id_number'];
    },
    documentCatalogRows() {
      const rows = [];
      this.sortedMotorPolicies.forEach((policy) => {
        if (!policy?.policyNo) return;
        rows.push({
          id: `motor-${policy.policyNo}`,
          productKey: 'motor',
          productLabel: 'Motor',
          title: this.motorCoverTitle(policy),
          meta: this.motorVehicleSummary(policy),
          policyNumber: String(policy.policyNo).trim(),
        });
      });
      this.sortedTravelPolicies.forEach((policy) => {
        const policyNumber = String(policy?.tqPolicyNumber || '').trim();
        if (!policyNumber) return;
        rows.push({
          id: `travel-${policy.quoteId || policyNumber}`,
          productKey: 'travel',
          productLabel: 'Travel',
          title: 'Travel Insurance',
          meta: this.travelDestinationLabel(policy),
          policyNumber,
        });
      });
      this.sortedPaPolicies.forEach((policy) => {
        const policyNumber = String(policy?.policyNumber || '').trim();
        if (!policyNumber) return;
        rows.push({
          id: `pa-${policy.quoteId || policyNumber}`,
          productKey: 'pa',
          productLabel: 'PA',
          title: 'Students PA',
          meta: policy.optionName || policy.proposerName || 'Personal Accident',
          policyNumber,
        });
      });
      return rows;
    },
    lineTabs() {
      return [
        { key: 'general', label: 'General' },
        { key: 'medical', label: 'Medical' },
      ];
    },
    productTabs() {
      return [
        {
          key: 'motor',
          label: 'Motor',
          icon: 'mdi-car-outline',
          count: this.policies.length,
          loading: this.motorLoading,
          recency: maxRecency(this.policies, motorRecencyKey),
        },
        {
          key: 'travel',
          label: 'Travel',
          icon: 'mdi-airplane',
          count: this.travelPolicies.length,
          loading: this.travelLoading,
          recency: maxRecency(this.travelPolicies, travelRecencyKey),
        },
        {
          key: 'pa',
          label: 'PA',
          icon: 'mdi-shield-account-outline',
          count: this.paPolicies.length,
          loading: this.paLoading,
          recency: maxRecency(this.paPolicies, paRecencyKey),
        },
        {
          key: 'documents',
          label: 'Documents',
          icon: 'mdi-file-document-outline',
          count: this.documentCatalogRows.length,
          loading: this.sourcesLoading && this.documentCatalogRows.length === 0,
          recency: 0,
        },
      ];
    },
  },
  watch: {
    '$route.query.focus': {
      handler(focus) {
        this.applyFocusProduct(focus);
        this.$nextTick(() => this.scrollToFocusedPolicy());
      },
    },
    customerClient: {
      handler(client) {
        if (client?.clientCode && this.policies.length === 0 && !this.motorLoading) {
          this.loadMotorPolicies();
        }
      },
    },
  },
  async mounted() {
    this.loadHealthPolicies();
    const clientTask = this.$store.dispatch('portalCache/fetchCustomerClient', { force: true });
    await Promise.all([
      this.loadPaPolicies(),
      this.loadTravelPolicies(),
      this.loadMotorPolicies(clientTask),
    ]);
    this.selectDefaultProduct();
    this.$nextTick(() => this.scrollToFocusedPolicy());
  },
  metaInfo: {
    title: 'My Policies',
  },
  methods: {
    renewLocation: customerPolicyRenewalLocation,
    selectDefaultProduct() {
      const focus = this.$route.query?.focus;
      if (this.applyFocusProduct(focus)) return;
      if (!this.hasAnyPolicies && this.healthPolicies.length) {
        this.selectedLine = 'medical';
        return;
      }
      if (this.productTouched) return;
      const current = this.productTabs.find((tab) => tab.key === this.selectedProduct);
      if (current && current.key !== 'documents' && current.count > 0) return;
      const withPolicies = this.productTabs
        .filter((tab) => tab.key !== 'documents' && tab.count > 0)
        .sort((a, b) => b.recency - a.recency);
      this.selectedProduct = withPolicies[0]?.key || 'motor';
    },
    applyFocusProduct(focus) {
      if (!focus || typeof focus !== 'string') return false;
      if (focus.startsWith('motor-')) {
        this.selectedProduct = 'motor';
        return true;
      }
      if (focus.startsWith('travel-')) {
        this.selectedProduct = 'travel';
        return true;
      }
      if (focus.startsWith('pa-')) {
        this.selectedProduct = 'pa';
        return true;
      }
      if (focus.startsWith('health-')) {
        this.selectedLine = 'medical';
        return true;
      }
      return false;
    },
    async loadHealthPolicies() {
      if (!this.nationalId) {
        this.healthPolicies = [];
        this.healthError = '';
        return;
      }
      this.healthLoading = true;
      this.healthError = '';
      try {
        this.healthPolicies = await fetchHealthPolicies(this.nationalId, this.policyRequestConfig());
      } catch {
        this.healthPolicies = [];
        this.healthError = 'Unable to load your health policies. Please try again.';
      } finally {
        this.healthLoading = false;
        if (String(this.$route.query?.focus || '').startsWith('health-')) {
          this.selectedLine = 'medical';
          this.$nextTick(() => this.scrollToFocusedPolicy());
        }
      }
    },
    async loadMotorPolicies(clientTask) {
      if (this.motorLoading) return;
      const { client, nationalId } = this;
      if (!client?.clientCode && !nationalId && !clientTask) return;

      this.motorLoading = true;
      try {
        this.policies = await fetchMotorPolicyRecords({
          client,
          clientPromise: clientTask,
          nationalId,
          requestConfig: this.policyRequestConfig(),
          onUpdate: (rows) => {
            this.policies = rows;
          },
        });
        this.$nextTick(() => this.prefetchPolicyDocuments('motor'));
      } catch {
        this.policies = [];
        this.$toast.error('Unable to load your motor policies. Please try again.', 'Policies');
      } finally {
        this.motorLoading = false;
      }
    },
    motorCoverTitle(policy) {
      return `${motorCoverLabel(policy?.coverType)} Insurance`;
    },
    motorVehicleSummary(policy) {
      return motorVehicleLine(policy);
    },
    policyNoForDocs(policyNo) {
      if (!policyNo) return '';
      return String(policyNo).trim();
    },
    formatPaDate(value) {
      if (!value) return '—';
      return moment(value).format('DD MMM YYYY');
    },
    formatPolicyNumber(value) {
      if (!value) return '—';
      return String(value).replace(/-/g, '/');
    },
    travelDestinationLabel(policy) {
      const parts = [];
      if (policy?.region) parts.push(policy.region);
      if (policy?.destinationCountry) parts.push(policy.destinationCountry);
      if (policy?.durationLabel) parts.push(policy.durationLabel);
      return parts.length ? parts.join(' · ') : 'Travel cover';
    },
    policyRequestConfig() {
      return { _skipErrorRedirect: true, _showErrorToast: false };
    },
    policyCardId(type, policy) {
      return policyFocusId(type, policy);
    },
    isPolicyFocused(type, policy) {
      return this.$route.query?.focus === policyFocusId(type, policy);
    },
    scrollToFocusedPolicy() {
      const focus = this.$route.query?.focus;
      if (!focus) return;
      const el = document.getElementById(focus);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    async loadPaPolicies() {
      const { nationalId } = this;
      const email = this.authenticatedUser?.email;
      if (!nationalId && !email) {
        this.paPolicies = [];
        return;
      }
      this.paLoading = true;
      try {
        const res = await listPaClientPolicies({ nationalId, email });
        this.paPolicies = res.success && Array.isArray(res.data) ? res.data : [];
        this.$nextTick(() => this.prefetchPolicyDocuments('pa'));
      } catch {
        this.paPolicies = [];
      } finally {
        this.paLoading = false;
      }
    },
    async loadTravelPolicies() {
      const email = this.authenticatedUser?.email;
      if (!email) {
        this.travelPolicies = [];
        return;
      }
      this.travelLoading = true;
      try {
        const res = await listTravelClientPolicies({ email });
        this.travelPolicies = res.success && Array.isArray(res.data) ? res.data : [];
        this.$nextTick(() => this.prefetchPolicyDocuments('travel'));
      } catch {
        this.travelPolicies = [];
      } finally {
        this.travelLoading = false;
      }
    },
    hasPolicyDocLookup(type, policy) {
      if (type === 'motor') return !!this.motorDocByPolicy[policy.policyNo];
      if (type === 'travel') return !!this.travelDocByPolicy[policy.tqPolicyNumber];
      if (type === 'pa') return !!this.paDocByPolicy[policy.policyNumber];
      return false;
    },
    prefetchPolicyDocuments(type) {
      let list = [];
      if (type === 'motor') list = this.sortedMotorPolicies;
      if (type === 'travel') list = this.sortedTravelPolicies;
      if (type === 'pa') list = this.sortedPaPolicies;
      list.slice(0, DOC_PREFETCH_LIMIT).forEach((policy) => {
        if (this.hasPolicyDocLookup(type, policy)) return;
        if (type === 'motor' && policy.policyNo) this.loadMotorPolicyDocument(policy.policyNo);
        if (type === 'travel' && policy.tqPolicyNumber) this.loadTravelPolicyDocument(policy.tqPolicyNumber);
        if (type === 'pa' && policy.policyNumber) this.loadPaPolicyDocument(policy.policyNumber);
      });
    },
    isRenewable(policy) {
      if (!policy?.policyNo || !policy?.batchNo) return false;
      // Renew CTA lives only on motor cards; keep an explicit motor guard.
      const group = String(policy.productGroup || policy.product || '').toUpperCase();
      const isMotor = group.includes('MOTOR')
        || Number(policy.productCode) === 700
        || Number(policy.productCode) === 3437
        || /^0?10\/0?7/i.test(String(policy.policyNo || ''));
      if (!isMotor) return false;
      if (!policy.wet) return false;
      const expiry = new Date(policy.wet);
      if (Number.isNaN(expiry.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const windowStart = new Date(today);
      windowStart.setDate(windowStart.getDate() - 30);
      const windowEnd = new Date(today);
      windowEnd.setDate(windowEnd.getDate() + 90);
      return expiry >= windowStart && expiry <= windowEnd;
    },
    async loadMotorPolicyDocument(policyNo) {
      if (!policyNo) return;
      this.$set(this.motorDocLoading, policyNo, true);
      try {
        const result = await resolvePaPolicyDocument(this.policyNoForDocs(policyNo));
        this.$set(this.motorDocByPolicy, policyNo, result.document || null);
      } finally {
        this.$set(this.motorDocLoading, policyNo, false);
      }
    },
    async loadTravelPolicyDocument(policyNumber) {
      if (!policyNumber) return;
      this.$set(this.travelDocLoading, policyNumber, true);
      try {
        const result = await resolvePaPolicyDocument(policyNumber);
        this.$set(this.travelDocByPolicy, policyNumber, result.document || null);
      } finally {
        this.$set(this.travelDocLoading, policyNumber, false);
      }
    },
    async loadPaPolicyDocument(policyNumber) {
      if (!policyNumber) return;
      this.$set(this.paDocLoading, policyNumber, true);
      try {
        const result = await resolvePaPolicyDocument(policyNumber);
        this.$set(this.paDocByPolicy, policyNumber, result.document || null);
      } finally {
        this.$set(this.paDocLoading, policyNumber, false);
      }
    },
    async refreshDocumentsCatalog() {
      const rows = this.documentCatalogRows.filter((row) => this.isCatalogExpanded(row.id));
      const targets = rows.length
        ? rows
        : this.documentCatalogRows.filter((row) => !!this.catalogEntry(row.id));
      if (!targets.length) return;
      this.docsCatalogLoading = true;
      try {
        await Promise.all(targets.map((row) => this.loadCatalogDocument(row)));
      } finally {
        this.docsCatalogLoading = false;
      }
    },
    isCatalogExpanded(rowId) {
      return !!this.docsCatalogExpandedById[rowId];
    },
    async toggleCatalogRow(row) {
      if (!row || !row.id) return;
      const next = !this.isCatalogExpanded(row.id);
      this.$set(this.docsCatalogExpandedById, row.id, next);
      if (next && !this.catalogEntry(row.id) && !this.isCatalogLoading(row.id)) {
        await this.loadCatalogDocument(row);
      }
    },
    async loadCatalogDocument(row) {
      if (!row || !row.id || !row.policyNumber) return;
      this.$set(this.docsCatalogLoadingById, row.id, true);
      try {
        const result = await fetchPolicyDocuments(row.policyNumber);
        const documents = result.success ? (result.documents || []) : [];
        this.$set(this.docsCatalogById, row.id, {
          ready: documents.length > 0,
          documents,
          error: result.success ? null : (result.message || 'Unable to load documents'),
        });
      } finally {
        this.$set(this.docsCatalogLoadingById, row.id, false);
      }
    },
    catalogEntry(rowId) {
      return this.docsCatalogById[rowId] || null;
    },
    catalogDocuments(rowId) {
      const entry = this.catalogEntry(rowId);
      return (entry && entry.documents) || [];
    },
    catalogDocCount(rowId) {
      return this.catalogDocuments(rowId).length;
    },
    isCatalogLoading(rowId) {
      return !!this.docsCatalogLoadingById[rowId];
    },
    formatDocumentTypeLabel(doc) {
      const raw = String((doc && (doc.docType || doc.actualName)) || 'Document').trim();
      if (!raw) return 'Document';
      return raw
        .toLowerCase()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    isDocActionLoading(docId, action) {
      return this.docsFileActionById[docId] === action;
    },
    isDocActionBusy(docId) {
      return !!this.docsFileActionById[docId];
    },
    async openCatalogFile(doc) {
      if (!doc || !doc.id) return;
      this.$set(this.docsFileActionById, doc.id, 'view');
      try {
        const openResult = await openPolicyDocument(doc);
        if (!openResult.success && this.$toast) {
          this.$toast.error(openResult.message || 'Could not open document');
        }
      } finally {
        this.$delete(this.docsFileActionById, doc.id);
      }
    },
    async downloadCatalogFile(doc, policyNumber) {
      if (!doc || !doc.id) return;
      this.$set(this.docsFileActionById, doc.id, 'download');
      try {
        const dlResult = await downloadPolicyDocument(doc, policyNumber);
        if (!dlResult.success && this.$toast) {
          this.$toast.error(dlResult.message || 'Could not download document');
        }
      } finally {
        this.$delete(this.docsFileActionById, doc.id);
      }
    },
  },
};
</script>

<style scoped>
.v-application p {
   margin-bottom: 0px!important;
}
.notShown {
  visibility: hidden !important;
}

.policy-shimmer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 1rem;
}

.policy-shimmer-card {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 14px 16px;
  padding: 1.25rem;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  background: #fff;
}

.policy-shimmer-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
}

.policy-shimmer-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}

.policy-shimmer-line {
  height: 12px;
  width: 46%;
  border-radius: 999px;
}

.policy-shimmer-line--title {
  width: 28%;
  height: 16px;
}

.policy-shimmer-block {
  grid-column: 1 / -1;
  height: 64px;
  border-radius: 12px;
}

.shimmer {
  background: linear-gradient(90deg, #e8eef5 25%, #f8fafc 37%, #e8eef5 63%);
  background-size: 400% 100%;
  animation: policy-shimmer 1.2s ease infinite;
}

@keyframes policy-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

.policy-line-tabs {
  display: flex;
  gap: 1.75rem;
  margin: 0.25rem 0 0.15rem;
  border-bottom: 1px solid #e2e8f0;
}

.policy-line-tab {
  position: relative;
  margin: 0;
  padding: 2px 0 12px;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  appearance: none;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

.policy-line-tab:focus,
.policy-line-tab:focus-visible {
  outline: none;
  box-shadow: none;
}

.policy-line-tab--active {
  color: #184679;
}

.policy-line-tab--active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background: #184679;
}

.policy-product-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0.75rem 0 0.25rem;
}

.policy-product-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.policy-product-toggle:not(.policy-product-toggle--active):hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

.policy-product-toggle:focus-visible {
  outline: 2px solid #184679;
  outline-offset: 2px;
}

.policy-product-toggle--active {
  border-color: #184679;
  background: #184679;
  color: #fff;
}

.policy-product-toggle--active:hover {
  border-color: #123763;
  background: #123763;
}

.policy-product-toggle__count {
  min-width: 1.35rem;
  padding: 2px 7px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 800;
  text-align: center;
}

.policy-product-toggle--active .policy-product-toggle__count {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.pa-policies-section {
  margin-top: 1rem;
}

.policy-product-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 600;
}

.pa-section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
}

.pa-section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1E4276;
  letter-spacing: -0.01em;
}

.pa-section-subtitle {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #64748B;
}

.pa-policy-card {
  border: 1px solid #E2E8F0 !important;
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 4px 18px rgba(24, 70, 121, 0.06) !important;
}

.pa-policy-card__body {
  padding: 1.25rem 1.25rem 1rem;
}

@media (min-width: 768px) {
  .pa-policy-card__body {
    padding: 1.5rem 1.75rem 1.25rem;
  }
}

.pa-policy-card__hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 1.25rem;
}

.pa-policy-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #E0F4FD 0%, #F0F9FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pa-policy-card__intro {
  flex: 1;
  min-width: 0;
}

.pa-policy-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pa-policy-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1E4276;
}

.pa-policy-card__status {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #15803D;
  background: #DCFCE7;
  border-radius: 999px;
  padding: 3px 10px;
}

.pa-policy-card__option {
  margin: 4px 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #00AEEF;
}

.pa-policy-card__proposer {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #64748B;
}

.pa-section-doc-hint {
  margin: 6px 0 0;
  font-size: 0.8125rem;
  color: #64748B;
  line-height: 1.45;
  max-width: 42rem;
}

.docs-catalog-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.docs-catalog-card {
  border: 1px solid #E2E8F0 !important;
  border-radius: 16px !important;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.docs-catalog-card--open {
  border-color: #BFDBFE !important;
  box-shadow: 0 1px 2px rgba(30, 66, 118, 0.06);
}

.docs-catalog-row__header {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border: 0;
  background: #F8FAFC;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.docs-catalog-row__header:hover {
  background: #F1F5F9;
}

.docs-catalog-card--open .docs-catalog-row__header {
  border-bottom: 1px solid #EEF2F6;
  background: #F8FAFC;
}

.docs-catalog-row__chevron {
  flex-shrink: 0;
  margin-top: 2px;
}

.docs-catalog-row__main {
  flex: 1 1 auto;
  min-width: 0;
}

.docs-catalog-row__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
  padding-top: 2px;
}

.docs-catalog-row__product {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 2px 8px;
  border-radius: 999px;
  background: #EFF6FF;
  color: #1E40AF;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.docs-catalog-row__title {
  margin: 6px 0 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1E4276;
}

.docs-catalog-row__meta,
.docs-catalog-row__policy {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: #64748B;
}

.docs-catalog-row__policy {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #334155;
}

.docs-catalog-panel {
  background: #fff;
}

.docs-catalog-loading,
.docs-catalog-empty {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  color: #64748B;
  font-size: 0.875rem;
}

.docs-catalog-files {
  display: flex;
  flex-direction: column;
  max-height: min(420px, 55vh);
  overflow-y: auto;
}

.docs-catalog-file {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid #EEF2F6;
}

.docs-catalog-file:last-child {
  border-bottom: none;
}

.docs-catalog-file__icon {
  flex-shrink: 0;
}

.docs-catalog-file__info {
  flex: 1 1 180px;
  min-width: 0;
}

.docs-catalog-file__name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1E4276;
}

.docs-catalog-file__meta {
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: #64748B;
  word-break: break-word;
}

.docs-catalog-file__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.pa-doc-fetch-btn {
  display: inline-flex;
  align-items: center;
  margin-top: 0;
  padding: 0;
  border: none;
  background: none;
  color: #0284C7;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.pa-doc-fetch-btn:hover {
  color: #184679;
}

.pa-policy-card__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-top: 4px;
}

@media (min-width: 640px) {
  .pa-policy-card__actions {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 20px;
  }
}

.pa-policy-card__details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 14px 16px;
  background: #F8FAFC;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .pa-policy-card__details {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
}

.pa-detail-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pa-detail-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94A3B8;
}

.pa-detail-value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1E4276;
  line-height: 1.35;
}

.pa-detail-value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  word-break: break-all;
}

.pa-detail-value--premium {
  color: #184679;
  font-size: 1rem;
}

.pa-policy-card__icon--travel {
  background: linear-gradient(135deg, #E0F2FE 0%, #F0F9FF 100%);
}

.pa-policy-card__icon--motor {
  background: linear-gradient(135deg, #E8EEF7 0%, #F4F7FB 100%);
}

.motor-policy-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #FFEDD5;
  color: #9A3412;
  font-size: 0.875rem;
  font-weight: 600;
}

.motor-policy-banner__action {
  margin-left: auto;
  color: #C2410C;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.motor-policy-view-link {
  display: inline-flex;
  align-items: center;
  color: #184679;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
}

.motor-policy-view-link:hover {
  color: #0284C7;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.motor-renew-link {
  display: inline-flex;
  align-items: center;
  color: #C2410C;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
  padding: 4px 12px;
  border: 1.5px solid #FDBA74;
  border-radius: 8px;
  background: #FFF7ED;
  transition: all 0.15s;
}

.motor-renew-link:hover {
  background: #FFEDD5;
  border-color: #FB923C;
}

.pa-policy-card__details--travel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 0.75rem;
  padding: 16px 18px;
}

.travel-detail-summary,
.travel-detail-refs {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

@media (min-width: 640px) {
  .travel-detail-summary {
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    align-items: end;
  }

  .travel-detail-refs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.travel-detail-refs {
  padding-top: 14px;
  border-top: 1px solid #E2E8F0;
}

.pa-policy-card__details--travel .pa-detail-block--premium {
  text-align: left;
}

@media (min-width: 640px) {
  .pa-policy-card__details--travel .pa-detail-block--premium {
    text-align: right;
  }
}

.pa-policy-card__details--travel .pa-detail-value--mono {
  word-break: normal;
  overflow-wrap: anywhere;
}

.pa-policy-card--travel .pa-policy-card__option {
  color: #0284C7;
}

@media (min-width: 640px) {
  .pa-detail-block--premium {
    text-align: right;
  }
}

/* Domestic Package */
.home-policies-section {
  margin-top: 2.5rem;
}

.home-section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
}

.home-section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1E4276;
  letter-spacing: -0.01em;
}

.home-section-subtitle {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #64748B;
}

.home-policy-card {
  border: 1px solid #E2E8F0 !important;
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 4px 18px rgba(24, 70, 121, 0.06) !important;
}

.home-policy-card--renewal {
  border-color: #FED7AA !important;
}

.home-renewal-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #FFF7ED;
  border-bottom: 1px solid #FFEDD5;
  color: #C2410C;
  font-size: 0.84rem;
  font-weight: 600;
}

.home-policy-card__body {
  padding: 1.25rem 1.25rem 1rem;
}

@media (min-width: 768px) {
  .home-policy-card__body {
    padding: 1.5rem 1.75rem 1.25rem;
  }
}

.home-policy-card__hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 1.25rem;
}

.home-policy-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #E0F4FD 0%, #F0F9FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.home-policy-card__icon--renewal {
  background: linear-gradient(135deg, #FFEDD5 0%, #FFF7ED 100%);
}

.home-policy-card__intro {
  flex: 1;
  min-width: 0;
}

.home-policy-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.home-policy-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1E4276;
}

.home-policy-card__status {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #15803D;
  background: #DCFCE7;
  border-radius: 999px;
  padding: 3px 10px;
}

.home-policy-card__status--renewal {
  color: #C2410C;
  background: #FFEDD5;
}

.home-policy-card__option {
  margin: 4px 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #00AEEF;
}

.home-policy-card__details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 14px 16px;
  background: #F8FAFC;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .home-policy-card__details {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
}

.home-detail-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.home-detail-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94A3B8;
}

.home-detail-value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1E4276;
  line-height: 1.35;
}

.home-detail-value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  word-break: break-all;
}

.home-detail-value--premium {
  color: #184679;
  font-size: 1rem;
}

@media (min-width: 640px) {
  .home-detail-block--highlight {
    text-align: right;
  }
}

.home-policy-card__actions {
  margin-top: 0.25rem;
}

.home-policy-card__actions--split {
  display: flex;
  gap: 10px;
}

.home-action-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  border-radius: 10px !important;
}

.home-action-btn--outline {
  flex: 1;
}

.claim-dialog-header {
  padding: 8px 4px 0;
}

.claim-dialog-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1E4276;
}

.claim-dialog-sub {
  margin: 6px 0 0;
  font-size: 0.84rem;
  color: #64748B;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.policy-card--focused {
  border-color: #184679 !important;
  box-shadow: 0 0 0 3px rgba(24, 70, 121, 0.22), 0 8px 24px rgba(24, 70, 121, 0.12) !important;
  scroll-margin-top: 96px;
}
</style>
